/**
 * Iter214 — Shared Log-Call modal.
 *
 * Slick 3-second call logger. Wired to:
 *   • Smith Kanban card (phone icon)
 *   • Sales Priorities table Quick Actions
 *
 * Fires POST /api/smith/activity/prospects/{pid}/log-call → auto-updates
 * worked_status + Last Contact + optional follow-up.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, X, Loader2, CalendarClock, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OUTCOMES = [
  "Connected",         "No Answer",           "Voicemail",
  "Wrong Number",      "Gatekeeper",          "Decision Maker Reached",
  "Interested",        "Not Interested",      "Call Back Requested",
];

const FOLLOWUP_PRESETS = [
  { id: "today",     label: "Today",     hours: 4 },
  { id: "tomorrow",  label: "Tomorrow",  hours: 24 },
  { id: "3days",     label: "3 Days",    hours: 72 },
  { id: "1week",     label: "1 Week",    hours: 168 },
  { id: "2weeks",    label: "2 Weeks",   hours: 336 },
];

export default function LogCallModal({ prospectId, companyName, onClose, onLogged }) {
  const [outcome, setOutcome] = useState("Connected");
  const [notes, setNotes]     = useState("");
  const [followup, setFollowup] = useState(null);
  const [saving, setSaving]   = useState(false);

  const auth = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };

  const save = async () => {
    setSaving(true);
    let followUpAt = null;
    if (followup) {
      const preset = FOLLOWUP_PRESETS.find((p) => p.id === followup);
      if (preset) {
        const dt = new Date(Date.now() + preset.hours * 3600 * 1000);
        followUpAt = dt.toISOString();
      }
    }
    try {
      const r = await axios.post(
        `${API}/smith/activity/prospects/${prospectId}/log-call`,
        { outcome, notes: notes || null, follow_up_at: followUpAt },
        auth,
      );
      toast.success(`Call registrada · ${outcome}`);
      onLogged?.(r.data?.interaction);
      onClose?.();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Falha ao registrar call");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center"
         data-testid="log-call-modal" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70" />
      <div className="relative w-[520px] max-w-[92vw] rounded-2xl border border-sky-500/40 p-5 space-y-4"
           style={{ background: "#0F172A" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                 style={{ background: "linear-gradient(135deg,#0EA5E9 0%,#0369A1 100%)" }}>
              <Phone className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-[14px] font-bold text-white" data-testid="log-call-title">
                Log Call · {companyName || "Prospect"}
              </h3>
              <p className="text-[10.5px] text-slate-400">
                Registra automaticamente worked_status + Last Contact
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose}
            data-testid="log-call-close-btn"
            className="w-7 h-7 rounded border border-white/10 flex items-center justify-center text-slate-400 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        <div>
          <label className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold block mb-1">
            Outcome
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5"
               data-testid="log-call-outcome-grid">
            {OUTCOMES.map((o) => (
              <button key={o} type="button" onClick={() => setOutcome(o)}
                data-testid={`log-call-outcome-${o.toLowerCase().replace(/\s+/g,"-")}`}
                className={`h-8 px-2 rounded text-[11px] font-bold border ${outcome === o
                  ? "border-sky-500 bg-sky-500/15 text-sky-100"
                  : "border-white/10 text-slate-300 hover:border-sky-500/40"}`}>
                {o}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1 mb-1">
            <MessageSquare className="w-3 h-3" /> Notes
          </label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            rows={3}
            data-testid="log-call-notes"
            placeholder="Quick notes about the call…"
            className="w-full rounded-lg border border-white/15 bg-black/30 p-2 text-[12.5px] text-white placeholder:text-slate-500 focus:border-sky-500/60 focus:outline-none" />
        </div>

        <div>
          <label className="text-[10.5px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1 mb-1">
            <CalendarClock className="w-3 h-3" /> Schedule follow-up (optional)
          </label>
          <div className="flex flex-wrap gap-1.5" data-testid="log-call-followup-grid">
            {FOLLOWUP_PRESETS.map((p) => (
              <button key={p.id} type="button"
                onClick={() => setFollowup(followup === p.id ? null : p.id)}
                data-testid={`log-call-followup-${p.id}`}
                className={`h-8 px-3 rounded text-[11px] font-bold border ${followup === p.id
                  ? "border-amber-500 bg-amber-500/15 text-amber-100"
                  : "border-white/10 text-slate-300 hover:border-amber-500/40"}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 justify-end pt-3 border-t border-white/5">
          <button type="button" onClick={onClose}
            data-testid="log-call-cancel-btn"
            className="h-9 px-3 rounded-lg border border-white/15 text-[12px] font-bold text-slate-300 hover:text-white">
            Cancelar
          </button>
          <button type="button" onClick={save} disabled={saving}
            data-testid="log-call-save-btn"
            className="h-9 px-4 rounded-lg text-[12px] font-bold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg,#0EA5E9 0%,#0369A1 100%)" }}>
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Phone className="w-3.5 h-3.5" />}
            Save Call
          </button>
        </div>
      </div>
    </div>
  );
}
